import * as React from 'react';

import { Typography } from '@mui/material';

import {
  Statuses,
  SuitableForNumberID,
  LanguageID,
} from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';
import { CommonStyles } from '@kamernet/styles/CommonStyles';

import { SuitableForNumberLabels } from '@kamernet/modules/Listing/messages';
import {
  GenderTranslations,
  LanguageLabelTranslations,
  TenancyDurationTranslations,
  WorkStatusTranslations,
} from '@kamernet/modules/Listing/translations';

import { ListingDetailsContext } from '../../ListingDetailsContext';

import styles from './IdealTenant.module.css';

export const IdealTenant = () => {
  const { listingDetails } = ListingDetailsContext.useContext();
  const { T } = useIntl();

  const getCandidateAge = React.useCallback(
    (
      minAge: number | null | undefined,
      maxAge: number | null | undefined,
      suffix: string,
    ) => {
      if (!minAge || !maxAge) {
        return T('DMN_UNIMPORTANT');
      }
      const ageLimit = `${minAge} - ${maxAge}`;
      return `${ageLimit} ${suffix}`;
    },
    [T],
  );

  const getCandidateStatus = React.useCallback(
    (statusArray: number[] | null) => {
      if (!statusArray) {
        return T('DMN_EVERYONE_WELCOME');
      }
      const resultArray = statusArray.map(workstatus => {
        return T(WorkStatusTranslations[workstatus as Statuses]);
      });
      return resultArray.join(' , ');
    },
    [T],
  );

  const getLanguageSpoken = React.useCallback(
    (languageArray: number[] | null) => {
      if (!languageArray) {
        return T('DMN_EVERYONE_WELCOME');
      }
      const resultArray = languageArray.map(languageId => {
        return T(LanguageLabelTranslations[languageId as LanguageID]);
      });
      return resultArray.join(' , ');
    },
    [T],
  );

  return (
    <section className={styles.root}>
      <Typography className={CommonStyles.margin_bottom_2} variant="h5">
        {T('ldp.lld_ideal_tenant')}
      </Typography>
      <div className={styles.footer}>
        <div className={styles.cardRow}>
          <Typography variant="body2" color="secondary.accent">
            {T('LBL_NUMBER_OF_TENANTS')}
          </Typography>
          <Typography variant="body2">
            {listingDetails.suitableForNumberOfPersons === null
              ? T('DMN_NOT_IMPORTANT')
              : T(
                  'ldp.ideal_tenant.number_tenants.x_persons',
                  SuitableForNumberLabels[
                    (listingDetails.suitableForNumberOfPersons as SuitableForNumberID) ||
                      1
                  ],
                )}
          </Typography>
        </div>
        <div className={styles.cardRow}>
          <Typography variant="body2" color="secondary.accent">
            {T('LBL_AGE')}
          </Typography>
          <Typography variant="body2">
            {getCandidateAge(
              listingDetails.candidateMinAgeId,
              listingDetails.candidateMaxAgeId,
              T('LBL_ROOM_ADVERT_DETAILS_YEARS'),
            )}
          </Typography>
        </div>
        <div className={styles.cardRow}>
          <Typography variant="body2" color="secondary.accent">
            {T('LBL_GLOBAL_GENDER')}
          </Typography>
          <Typography variant="body2">
            {listingDetails.candidateGenderId
              ? T(GenderTranslations[listingDetails.candidateGenderId])
              : T('DMN_EVERYONE_WELCOME')}
          </Typography>
        </div>
        <div className={styles.cardRow}>
          <Typography variant="body2" color="secondary.accent">
            {T('LBL_OCCUPATION')}
          </Typography>
          <Typography variant="body2">
            {getCandidateStatus(listingDetails.candidateStatusId)}
          </Typography>
        </div>
        <div className={styles.cardRow}>
          <Typography variant="body2" color="secondary.accent">
            {T('LBL_MY_MESSAGES_SPOKEN_LANGUAGES')}
          </Typography>
          <Typography variant="body2">
            {getLanguageSpoken(listingDetails.desiredTenantLanguagesSpokenID)}
          </Typography>
        </div>
        <div className={styles.cardRow}>
          <Typography variant="body2" color="secondary.accent">
            {T('LBL_BQ_STAY_DURATION')}
          </Typography>
          <Typography variant="body2">
            {listingDetails.idealRentalPeriodId
              ? T(
                  TenancyDurationTranslations[
                    listingDetails.idealRentalPeriodId
                  ],
                )
              : T('DMN_NOT_IMPORTANT')}
          </Typography>
        </div>
      </div>
    </section>
  );
};
